import { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Compass,
  LibraryBig,
  LogOut,
  Settings,
  ShieldCheck,
  Sparkles,
  UserRound
} from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "./Logo";
import { OfflineBanner } from "./OfflineBanner";

const navItems = [
  { to: "/", label: "学习库", icon: LibraryBig },
  { to: "/discover", label: "发现", icon: Compass },
  { to: "/settings", label: "设置", icon: Settings }
];

function pageTitle(pathname: string) {
  if (pathname.startsWith("/talks/") && pathname.endsWith("/edit")) return "字幕编辑";
  if (pathname.startsWith("/talks/")) return "播放学习";
  if (pathname.startsWith("/discover")) return "发现";
  if (pathname.startsWith("/settings")) return "设置";
  return "学习库";
}

function isLibraryRoute(pathname: string) {
  return pathname === "/" || pathname.startsWith("/talks") || pathname === "/library";
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const immersive = location.pathname.startsWith("/talks/");
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.clear();
      navigate("/");
    }
  });

  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0 });
  }, [location.pathname, location.hash]);

  return (
    <div className={`v21-app ${immersive ? "is-immersive" : ""}`}>
      <OfflineBanner />

      <div className="v21-layout">
        <aside className="v21-sidebar">
          <Logo />

          <nav className="v21-side-nav" aria-label="桌面主导航">
            {navItems.map(({ to, label, icon: Icon }) => {
              const active =
                to === "/" ? isLibraryRoute(location.pathname) : location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`v21-side-link ${active ? "is-active" : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon size={18} strokeWidth={1.8} />
                  <span>{label}</span>
                </Link>
              );
            })}
          </nav>

          <section className="v21-side-room">
            <span className="v21-side-caption">PRIVATE ROOM</span>
            <div className="v21-side-room-card">
              <span className="v21-room-icon">
                <ShieldCheck size={18} />
              </span>
              <div>
                <strong>私享学习室</strong>
                <p>视频、字幕和学习进度只属于你。</p>
              </div>
            </div>
          </section>

          <div className="v21-sidebar-bottom">
            <div className="v21-side-status">
              <i />
              本地媒体 · 云端学习
            </div>
            <div className="v21-profile-row">
              <span className="v21-avatar">
                <UserRound size={16} />
              </span>
              <span>
                <strong>个人空间</strong>
                <small>单用户模式</small>
              </span>
              <button
                type="button"
                onClick={() => logout.mutate()}
                aria-label="退出登录"
                title="退出登录"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </aside>

        <section className="v21-workspace">
          {!immersive && (
            <header className="v21-toolbar">
              <div className="v21-breadcrumb">
                <span>我的学习室</span>
                <ChevronRight size={13} />
                <strong>{pageTitle(location.pathname)}</strong>
              </div>
              <div className="v21-toolbar-actions">
                <span className="v21-top-status">
                  <Sparkles size={13} />
                  私享空间
                </span>
              </div>
            </header>
          )}

          <main className="v21-main">
            <Outlet />
          </main>
        </section>
      </div>

      {!immersive && (
        <header className="v21-mobile-header">
          <Logo />
          <button
            type="button"
            className="v21-mobile-logout"
            onClick={() => logout.mutate()}
            aria-label="退出登录"
          >
            <LogOut size={18} />
          </button>
        </header>
      )}

      <nav className="v21-mobile-nav" aria-label="移动端主导航">
        {navItems.map(({ to, label, icon: Icon }) => {
          const active =
            to === "/" ? isLibraryRoute(location.pathname) : location.pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              className={`v21-mobile-nav-link ${active ? "is-active" : ""}`}
              aria-current={active ? "page" : undefined}
            >
              <Icon size={20} strokeWidth={1.75} />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
